import {
  AlertTriangleIcon,
  ExternalLinkIcon,
  FileTextIcon,
  HourglassIcon,
  TrashIcon,
} from '@assets/icons';
import { CopyIconButton, IconButton } from '@components/button';
import { Group } from '@components/layout';
import { ExternalLink } from '@components/link';
import { useConfirmation } from '@components/modal';
import { Typography } from '@components/typography';
import { Inpaint, useDeleteInpaintMutation } from '@generated/graphql';
import { formatTime } from '@helper-functions/time';
import { Box, Stack } from '@mui/material';
import { useNotifications } from '@notifications/Notifications';
import { map } from 'lodash';
import { Dispatch, SetStateAction } from 'react';

type InpaintPreviewProps = {
  workspaceId: string;
  inpaint: Inpaint;
  refetch: VoidFunction;
  setFolderHistory: Dispatch<SetStateAction<string[]>>;
};

export const InpaintPreview = ({
  workspaceId,
  inpaint,
  refetch,
  setFolderHistory,
}: InpaintPreviewProps) => {
  const { useAsyncNotification } = useNotifications();
  const { showConfirmation } = useConfirmation();
  const [deleteInpaint] = useDeleteInpaintMutation();

  const handleInpaintDeleteClick = async () => {
    await showConfirmation({
      message: 'Are you sure to clear the inpaint?',
      affirmText: 'Yes, clear',
      onAffirm: useAsyncNotification('Successfully cleared inpaint.', async () => {
        await deleteInpaint({
          variables: {
            volumeId: inpaint.volumeId,
            inpaintId: inpaint.inpaintId,
          },
        });
        refetch();
      }),
    });
  };

  return (
    <Box sx={{ mr: 2 }}>
      <Group padding={4} height="50px" alignItems="center" justifyContent="space-between" gap={1}>
        <Typography variant="body2" flex={1}>
          Inpaint
        </Typography>
        <Typography
          variant="body2"
          tooltip={
            <Stack>
              <Typography>{`Location: ${inpaint.location}`}</Typography>
              <Typography>{`Input type: ${inpaint.inputType}`}</Typography>
              <Typography>{`Output: ${inpaint.destination}`}</Typography>
              <Typography>{`Output type: ${inpaint.outputType}`}</Typography>
              <Typography>{`Dilation: ${inpaint.dilation}`}</Typography>
            </Stack>
          }
          flex={2}
          handleOverFlow
        >
          {formatTime(inpaint.createdAt, 'date') || '...'}
        </Typography>
        <Box sx={{ minWidth: '30px' }}>
          <CopyIconButton value={inpaint.inpaintId} />
        </Box>
        <Box sx={{ minWidth: '30px' }}>
          {inpaint.status === 'failed' && (
            <IconButton
              Icon={AlertTriangleIcon}
              variant="error"
              size={20}
              onClick={() => {
                if (typeof window !== 'undefined') {
                  window.open(
                    `/workspaces/${workspaceId}/assets/${inpaint.volumeId}/${inpaint.inpaintId}/logs`,
                    '_blank',
                  );
                }
              }}
            />
          )}
          {inpaint.status === 'success' && (
            <IconButton
              onClick={() => {
                if (inpaint.destination) {
                  setFolderHistory(map(inpaint.destination.split('/'), (folder) => `${folder}/`));
                }
              }}
              Icon={ExternalLinkIcon}
              size={20}
            />
          )}
          {['queued', 'starting', 'running'].includes(inpaint.status) && (
            <IconButton onClick={() => {}} Icon={HourglassIcon} size={20} />
          )}
        </Box>
        <ExternalLink
          unstyled
          href={`/workspaces/${workspaceId}/assets/${inpaint.volumeId}/${inpaint.inpaintId}/logs`}
        >
          <IconButton tooltip="View logs" Icon={FileTextIcon} size={20} />
        </ExternalLink>
        <Box sx={{ minWidth: '30px' }}>
          <IconButton
            onClick={handleInpaintDeleteClick}
            tooltip="Clear inpaint"
            Icon={TrashIcon}
            size={20}
            data-cy="Volume-Inpaint-Job-Preview-Info-Delete-Icon"
          />
        </Box>
      </Group>
    </Box>
  );
};
